/**
 * features/compliance/ComplianceRegister.tsx
 * Statutory document register with an "Upcoming renewals" summary
 * (sorted by soonest, with status) and a full list where each record
 * can carry an uploaded copy to download later.
 */
import { useMemo, useState } from 'react';
import { ShieldCheck, Plus, Download, CalendarClock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useCompliance } from './useCompliance';
import { ComplianceItemEditor } from './ComplianceItemEditor';
import { KIND_LABEL, statusOf, daysUntil, fmtDate, type RenewalStatus } from './complianceMeta';
import type { ComplianceItem } from '@/types';
import { Button, Card, Badge, Spinner } from '@/components/ui/primitives';

const STATUS_BADGE: Record<RenewalStatus, { tone: 'success' | 'warning' | 'danger' | 'neutral'; label: string }> = {
  valid: { tone: 'success', label: 'Valid' },
  soon: { tone: 'warning', label: 'Renew soon' },
  expired: { tone: 'danger', label: 'Expired' },
  none: { tone: 'neutral', label: 'No expiry' },
};

function renewalLine(n: number | null): string {
  if (n === null) return '';
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'} overdue`;
  if (n === 0) return 'Due today';
  return `in ${n} day${n === 1 ? '' : 's'}`;
}

export function ComplianceRegister() {
  const { items, loading } = useCompliance();
  const [editor, setEditor] = useState<ComplianceItem | null | 'new'>(null);

  const renewals = useMemo(
    () =>
      items
        .filter((i) => i.expiryDate)
        .sort((a, b) => (a.expiryDate! < b.expiryDate! ? -1 : 1)),
    [items],
  );

  if (loading) return <Spinner label="Loading compliance register…" />;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          Add document
        </Button>
      </div>

      {/* Upcoming renewals */}
      <Card accent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <CalendarClock size={18} className="text-brand" />
          <h3 className="font-semibold text-primary dark:text-gray-100">Important renewal dates</h3>
        </div>
        {renewals.length === 0 ? (
          <p className="text-sm text-primaryLight dark:text-gray-400">
            No dated records yet. Add your Tax Clearance, Annual Return or Trade Licence to track renewals.
          </p>
        ) : (
          <div className="space-y-2">
            {renewals.map((i) => {
              const st = statusOf(i);
              const n = daysUntil(i.expiryDate);
              const Icon = st === 'expired' ? AlertTriangle : st === 'soon' ? CalendarClock : CheckCircle2;
              const color = st === 'expired' ? 'text-red-600' : st === 'soon' ? 'text-amber-600' : 'text-green-600';
              return (
                <div key={i.id} className="flex items-center justify-between gap-3 rounded-xl bg-black/[0.03] px-3 py-2.5 dark:bg-white/5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Icon size={16} className={color} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-primary dark:text-gray-100">{i.title}</div>
                      <div className="text-xs text-primaryLight dark:text-gray-400">
                        Expires {fmtDate(i.expiryDate)} · {renewalLine(n)}
                      </div>
                    </div>
                  </div>
                  <Badge tone={STATUS_BADGE[st].tone}>{STATUS_BADGE[st].label}</Badge>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Full register */}
      {items.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <ShieldCheck size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No documents yet — add your certificate of incorporation, tax clearance and licences.
          </p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Document</th>
                <th className="px-4 py-3 font-semibold">Reference</th>
                <th className="px-4 py-3 font-semibold">Issued</th>
                <th className="px-4 py-3 font-semibold">Expires</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">File</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const st = statusOf(i);
                return (
                  <tr
                    key={i.id}
                    onClick={() => setEditor(i)}
                    className="cursor-pointer border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5"
                  >
                    <td className="px-4 py-3">
                      <div className="font-semibold text-primary dark:text-gray-100">{i.title}</div>
                      <div className="text-xs text-primaryLight dark:text-gray-400">{KIND_LABEL[i.kind]}{i.authority ? ` · ${i.authority}` : ''}</div>
                    </td>
                    <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{i.reference ?? '—'}</td>
                    <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(i.issueDate)}</td>
                    <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(i.expiryDate)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={STATUS_BADGE[st].tone}>{STATUS_BADGE[st].label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {i.attachmentUrl ? (
                        <a
                          href={i.attachmentUrl}
                          target="_blank"
                          rel="noreferrer"
                          download
                          title="Download"
                          className="inline-flex rounded-lg p-1.5 text-primaryLight transition hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5"
                        >
                          <Download size={16} />
                        </a>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && <ComplianceItemEditor item={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />}
    </div>
  );
}
